/// GPS literacy — the difference between what a chartplotter shows
/// and what the seabed is doing, plus the failure modes every
/// mariner with electronic kit should know about. Reference data
/// only; the apps/web layer renders this as a series of cards.

// ─── Coordinate formats + datums ────────────────────────────────

export interface CoordinateFormat {
  /// e.g. "Decimal degrees (DD)".
  name: string;
  /// One-line summary of the format.
  summary: string;
  /// Example showing 50°23.5'N 4°10.7'W in this format.
  example: string;
  /// When you'd see / use this format.
  use: string;
}

/// 50°23.5'N 4°10.7'W — Plymouth Sound — used as the canonical
/// reference example so the player can compare formats at a glance.
export const COORDINATE_FORMATS: readonly CoordinateFormat[] = [
  {
    name: "Decimal degrees (DD)",
    summary:
      "Single number per axis. Easiest to copy/paste into a chartplotter or share over voice.",
    example: "50.39167°N  4.17833°W",
    use: "Default output of most smartphone GPS apps. Common in URLs and digital exchanges.",
  },
  {
    name: "Degrees + decimal minutes (DDM)",
    summary:
      "Hours-minutes style. The maritime standard — every Admiralty chart, almanac, and almost every chartplotter uses this.",
    example: "50°23.5'N  4°10.7'W",
    use: "Use this on the chart, in your logbook, and when reading positions over VHF.",
  },
  {
    name: "Degrees, minutes, seconds (DMS)",
    summary: "Older sexagesimal format. Less common at sea — typically aviation / land surveys.",
    example: "50°23'30\"N  4°10'42\"W",
    use: "Rare on modern marine kit. Be careful: '30' as seconds vs '0.30' as decimal-minutes is a 12 m error.",
  },
];

export interface Datum {
  abbreviation: string;
  name: string;
  description: string;
  region: string;
  shiftFromWgs84: string;
}

export const DATUMS: readonly Datum[] = [
  {
    abbreviation: "WGS84",
    name: "World Geodetic System 1984",
    description:
      "Global datum used by GPS, modern Admiralty charts, AIS, and chartplotters. Default everywhere unless explicitly stated otherwise.",
    region: "Worldwide",
    shiftFromWgs84: "0 m by definition.",
  },
  {
    abbreviation: "OSGB36",
    name: "Ordnance Survey Great Britain 1936",
    description:
      "UK land-mapping datum. Some older Admiralty charts of UK waters were drawn on OSGB36; modern editions are on WGS84 but the shift can still apply to overlay imagery.",
    region: "UK + Ireland",
    shiftFromWgs84: "Up to ~120 m east of WGS84 in the south of England.",
  },
  {
    abbreviation: "ED50",
    name: "European Datum 1950",
    description:
      "Older European datum. Some Mediterranean and North-Sea charts still note 'positions on this chart are on ED50' in the title block.",
    region: "Europe + Mediterranean",
    shiftFromWgs84: "Up to ~200 m east-north-east of WGS84.",
  },
  {
    abbreviation: "NAD27 / NAD83",
    name: "North American Datum",
    description:
      "US/Canadian datums. NAD83 is functionally identical to WGS84 (sub-metre); NAD27 differs by tens to hundreds of metres.",
    region: "USA + Canada",
    shiftFromWgs84:
      "NAD83 ≈ 0. NAD27 up to ~250 m, varying widely by region.",
  },
];

export const DATUM_NOTE =
  "Always check the chart's datum statement (title block). Set the chartplotter to match the chart in use, OR convert positions before plotting. A GPS fix in WGS84 plotted onto an OSGB36 chart can put you a couple of cables off your real position — enough to put you on a rock.";

// ─── Track-vector terminology ───────────────────────────────────

export interface TrackTerm {
  abbreviation: string;
  name: string;
  meaning: string;
  /// Example sentence the mariner would say.
  example: string;
}

export const TRACK_TERMS: readonly TrackTerm[] = [
  {
    abbreviation: "COG",
    name: "Course Over Ground",
    meaning:
      "Direction the vessel is moving across the seabed — what the GPS reports. Combines heading, tide, and leeway.",
    example: '"COG is 075° — we\'re being set north of our heading."',
  },
  {
    abbreviation: "SOG",
    name: "Speed Over Ground",
    meaning:
      "Speed the vessel is moving across the seabed. The GPS-reported speed. Distinct from boat speed through the water.",
    example: '"SOG 6.8 knots, log 7.2 — we\'re punching half a knot of foul tide."',
  },
  {
    abbreviation: "CTS",
    name: "Course To Steer",
    meaning:
      "Heading the helmsman steers to make good a desired track in the presence of tide. Calculated, not measured.",
    example: '"CTS 280° to lay the next waypoint allowing for the ebb."',
  },
  {
    abbreviation: "HDG",
    name: "Heading",
    meaning:
      "Direction the bow is pointing — what the compass shows. Differs from COG by the tide + leeway vector.",
    example: '"HDG 270°, COG 285° — there\'s 15° of tidal set to the north."',
  },
  {
    abbreviation: "BTW",
    name: "Bearing To Waypoint",
    meaning:
      "Direction from the current position to the next waypoint. Updates continuously as the vessel moves.",
    example: '"BTW 092° — turn 17° to starboard to head straight at it."',
  },
  {
    abbreviation: "XTE",
    name: "Cross-Track Error",
    meaning:
      "Perpendicular distance off the planned route line — positive to starboard, negative to port (or vice versa, depending on plotter).",
    example: '"XTE 0.3 nm to port — alter to starboard to close the line."',
  },
  {
    abbreviation: "ETA",
    name: "Estimated Time of Arrival",
    meaning:
      "Plotter's projection of when you'll reach the next waypoint, based on current SOG. Doesn't account for changing tide.",
    example: '"ETA at WP3 is 1340 — fits the tidal gate at the headland."',
  },
];

// ─── Position quality ───────────────────────────────────────────

export interface QualityMetric {
  abbreviation: string;
  name: string;
  description: string;
  /// What's good / acceptable / bad in plain terms.
  scale: string;
}

export const QUALITY_METRICS: readonly QualityMetric[] = [
  {
    abbreviation: "HDOP",
    name: "Horizontal Dilution of Precision",
    description:
      "Geometric factor describing how the satellites are arranged around the receiver. Lower is better — satellites spread evenly across the sky give a tighter fix than ones clustered overhead.",
    scale: "< 1 excellent · 1-2 good · 2-5 moderate · 5-10 fair · > 10 poor",
  },
  {
    abbreviation: "PDOP",
    name: "Position DOP (3D)",
    description:
      "Same idea as HDOP but for a three-dimensional fix. Usually slightly larger than HDOP. The number a chartplotter shows under 'fix quality'.",
    scale: "< 4 acceptable · > 6 questionable",
  },
  {
    abbreviation: "SBAS",
    name: "Satellite-Based Augmentation",
    description:
      "Differential GPS correction broadcast by geostationary satellites — EGNOS over Europe, WAAS over North America, MSAS over Japan. Improves accuracy from ~5 m to ~1-2 m.",
    scale: "Enabled by default on most modern chartplotters and phones.",
  },
  {
    abbreviation: "RTK",
    name: "Real-Time Kinematic",
    description:
      "Centimetre-accurate positioning using carrier-phase measurements + a fixed reference station. Used in surveying and increasingly in autopilot-assisted approaches.",
    scale: "Centimetres at the antenna. Overkill for most leisure use but coming.",
  },
];

// ─── Common errors / failure modes ──────────────────────────────

export interface FailureMode {
  id: string;
  title: string;
  description: string;
  mitigation: string;
}

export const FAILURE_MODES: readonly FailureMode[] = [
  {
    id: "urban-canyon",
    title: "Urban canyon / harbour entry",
    description:
      "Tall buildings (or harbour walls + cliffs) block low satellites and reflect signals — the receiver sees ghost satellites or loses lock entirely. Position can jump 50 m or more between fixes.",
    mitigation:
      "Don't blindly trust the plotter close to walls. Eyeball + transit bearings remain the primary fix when entering harbour.",
  },
  {
    id: "stopped-noise",
    title: "Stopped-vessel position noise",
    description:
      "When the boat isn't moving, the GPS still calculates a fix every second — but with normal receiver noise, consecutive fixes can scatter 5-10 m apart. COG and SOG become meaningless below ~1 knot.",
    mitigation:
      "Don't read off bearings or SOG when alongside or anchored — the values are noise. Use the heading reference instead.",
  },
  {
    id: "datum-mismatch",
    title: "Datum mismatch",
    description:
      "The chart and the chartplotter are set to different geodetic datums. Plotted position is off by tens to hundreds of metres in a consistent direction.",
    mitigation:
      "Always check the chart's datum statement. Match the plotter (or convert manually). WGS84 onto an OSGB36 chart can put you east of where you really are.",
  },
  {
    id: "jamming",
    title: "Intentional jamming",
    description:
      "GPS signals are weak (~20 W satellites, 20,000 km away). A truck driver's $30 jammer can blank GPS for several miles. Increasingly common around airports, ports, and military exercise areas.",
    mitigation:
      "If the plotter shows position drift or 'no fix', expect a jamming event. Switch to dead reckoning + visual fixes. Note the time and approximate location and report to the relevant authority.",
  },
  {
    id: "spoofing",
    title: "Spoofing",
    description:
      "A nearby transmitter overpowers the genuine satellite signal and feeds the receiver false positions. Documented in the Black Sea and around some ports. The receiver thinks it has a perfect fix — but it's wrong.",
    mitigation:
      "Cross-check GPS against radar, chart features, transit bearings. A position that contradicts the visible coastline is suspect.",
  },
  {
    id: "antenna-loss",
    title: "Antenna loss or cable failure",
    description:
      "Water ingress, a corroded connector, or a loose deck-feed will silently degrade the fix. The receiver may report a position but with high HDOP — or no fix at all.",
    mitigation:
      "Pre-departure check should include 'plotter shows a fix with HDOP under 2 before slipping lines'. A handheld GPS in the grab bag is the backup.",
  },
  {
    id: "stale-charts",
    title: "Stale electronic charts",
    description:
      "Vector charts on the plotter were last updated three years ago — new buoys are missing, depths haven't been re-surveyed, restricted areas may have changed.",
    mitigation:
      "Update chart cards in line with the manufacturer's schedule (typically annual). Cross-reference with current paper chart on safety-critical passages.",
  },
];

// ─── Best-practice rules of thumb ───────────────────────────────

export const BEST_PRACTICES: readonly string[] = [
  "GPS is a tool, not the boss. Use it alongside the chart, the compass, and Mark I eyeball — never instead of them.",
  "Take a fix the old-fashioned way at every watch change: a quick three-point compass bearing or transit gives a sanity check on the plotter's position.",
  "Set the plotter's datum to match the chart in use. Modern Admiralty charts are WGS84; older charts are not.",
  "Trust SOG and COG only when moving above ~1 knot. At anchor or stationary, the values are receiver noise.",
  "Carry a handheld GPS with its own batteries, separate from the fixed plotter. If the boat's electrics die, you still have a backup fix.",
  "Update electronic charts at least annually. Note the issue date in the logbook.",
  "Don't navigate by waypoint-to-waypoint headings without looking at what's BETWEEN the waypoints — the line might cross a sandbank.",
  "If the GPS position is inconsistent with what you see out the window, the GPS is wrong, not the world. Investigate before continuing.",
];
